import React, { useEffect, useState } from "react";
import { doc, updateDoc, arrayUnion, arrayRemove } from "firebase/firestore";
import { db } from "../../utils/init-firebase";
import {
  Box,
  Flex,
  HStack,
  VStack,
  Wrap,
  WrapItem,
  Text,
  Heading,
  Switch,
  Select,
  Input,
  FormControl,
  IconButton,
  Avatar,
  Tooltip,
  Tag,
  Button,
  SimpleGrid,
  Divider,
  useColorModeValue,
  useToast,
} from "@chakra-ui/react";
import { InfoOutlineIcon, ExternalLinkIcon } from "@chakra-ui/icons";
import { BsFillTrashFill } from "react-icons/bs";
import { FiCheck } from "react-icons/fi";
import { CreatableSelect } from "chakra-react-select";
import { useUserManagement } from "../../pages/EOS/Admin/hooks/useUserManagement";
import { getHubRolesMirror } from "./crmRoleMirroring";

/**
 * A single labeled setting row: label (+ optional info tooltip) on the left,
 * the control on the right. Keeps every toggle clearly identified so admins
 * always know what they are changing.
 */
function SettingRow({ label, tooltip, children }) {
  return (
    <Flex justify="space-between" align="center" py={2} minH="40px">
      <HStack spacing={1} pr={3}>
        <Text fontSize="sm" fontWeight="medium">
          {label}
        </Text>
        {tooltip && (
          <Tooltip fontSize="sm" placement="top" label={tooltip}>
            <InfoOutlineIcon color="gray.400" boxSize={3} />
          </Tooltip>
        )}
      </HStack>
      <Box>{children}</Box>
    </Flex>
  );
}

/**
 * A titled, bordered group of related settings.
 */
function SettingSection({ title, bg, borderColor, children }) {
  return (
    <Box borderWidth="1px" borderColor={borderColor} borderRadius="md" p={4} bg={bg}>
      <Text
        fontSize="xs"
        fontWeight="bold"
        textTransform="uppercase"
        letterSpacing="wide"
        color="gray.500"
        mb={1}
      >
        {title}
      </Text>
      <Divider mb={1} />
      {children}
    </Box>
  );
}

/**
 * UserAdminControls — the single source of truth for editing a user's Hub
 * permissions. Rendered both in the Admin page (master-detail) and on the
 * individual user Profile page, so behavior can never drift between the two.
 *
 * Props:
 *  - user: full Firestore user document data (must include `uid` + permission fields)
 *  - branchOptions: array of { label, value } for the Branch selector
 *  - allowDeactivate: boolean — whether to show the deactivate (trash) action
 *  - onRequestDeactivate: (uid, name) => void — opens the delete/deactivate dialog
 *  - onOpenProfile: (uid) => void — optional; shows an "Open full profile" link when provided
 */
export default function UserAdminControls({
  user,
  branchOptions = [],
  allowDeactivate = false,
  onRequestDeactivate,
  onOpenProfile,
}) {
  const toast = useToast();
  const { updateUserPermission } = useUserManagement();
  const [loadingPermissionId, setLoadingPermissionId] = useState(null);
  const [swagBudget, setSwagBudget] = useState(
    user && user["Swag Budget"] != null ? user["Swag Budget"] : ""
  );

  const cardBg = useColorModeValue("white", "gray.800");
  const borderColor = useColorModeValue("gray.200", "gray.600");
  const sectionBg = useColorModeValue("gray.50", "gray.700");
  const subtleColor = useColorModeValue("gray.500", "gray.400");

  // Reset the locally-edited swag budget whenever we switch to a different user.
  useEffect(() => {
    setSwagBudget(user && user["Swag Budget"] != null ? user["Swag Budget"] : "");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.uid]);

  if (!user) return null;

  const handleAdminChange = async (checked, fieldDescr, field) => {
    try {
      const status = checked ? "success" : "warning";
      const title = checked
        ? `${fieldDescr} permissions granted for ${user.name} `
        : `${fieldDescr} permissions revoked for ${user.name} `;
      await updateDoc(doc(db, "users", user.uid), {
        [field]: checked,
      });
      toast({ title, status, position: "top", isClosable: true });
    } catch (err) {
      toast({ title: "Error: " + err, status: "error", position: "top", isClosable: true });
    }
  };

  const handleCRMUserChange = async (checked) => {
    try {
      const userDoc = doc(db, "users", user.uid);
      if (checked === true) {
        // FHL Scheduling ships with CRM access (per-user kill switch stays available)
        const updates = { CRMUser: true, calSchedulingEnabled: true };
        if (!user.CRMRole) {
          // Default CRM role only — deliberately NOT mirrored into Hub
          // `Roles` (see crmRoleMirroring.js: the Hub "Loan Officer" role
          // re-scopes the legacy Submissions pages and broke CSC loan views).
          updates.CRMRole = "Loan Officer";
        }
        await updateDoc(userDoc, updates);
        toast({
          title: `CRM User access granted for ${user.name}`,
          status: "success",
          position: "top",
          isClosable: true,
        });
      } else {
        const updates = { CRMUser: false, CRMRole: null };
        const { removeFromRoles } = getHubRolesMirror(user.CRMRole, null);
        if (removeFromRoles) {
          updates.Roles = arrayRemove(removeFromRoles);
        }
        await updateDoc(userDoc, updates);
        toast({
          title: `CRM User access revoked for ${user.name}`,
          status: "warning",
          position: "top",
          isClosable: true,
        });
      }
    } catch (err) {
      toast({ title: "Error: " + err, status: "error", position: "top", isClosable: true });
    }
  };

  const handleCRMRoleChange = async (newRole) => {
    try {
      const userDoc = doc(db, "users", user.uid);
      const oldRole = user.CRMRole;
      const updates = { CRMRole: newRole || null };
      const { addToRoles, removeFromRoles } = getHubRolesMirror(oldRole, newRole || null);

      if (removeFromRoles) {
        updates.Roles = arrayRemove(removeFromRoles);
      }
      await updateDoc(userDoc, updates);
      if (addToRoles) {
        // Separate write: Firestore rejects two array transforms on the same
        // field in one update.
        await updateDoc(userDoc, { Roles: arrayUnion(addToRoles) });
      }

      toast({
        title: `CRM Role updated to ${newRole || "none"} for ${user.name}`,
        status: "success",
        position: "top",
        isClosable: true,
      });
    } catch (err) {
      toast({ title: "Error: " + err, status: "error", position: "top", isClosable: true });
    }
  };

  const handleDTCLoanOfficerChange = async (checked) => {
    try {
      const userDoc = doc(db, "users", user.uid);
      if (checked) {
        await updateDoc(userDoc, {
          isDTCLoanOfficer: true,
          Roles: arrayUnion("DTC Loan Officer"),
        });
        toast({
          title: `DTC Loan Officer enabled for ${user.name}`,
          description:
            "Lost deal workflow will now prompt for reason before marking deals as lost",
          status: "success",
          position: "top",
          isClosable: true,
        });
      } else {
        await updateDoc(userDoc, {
          isDTCLoanOfficer: false,
          Roles: arrayRemove("DTC Loan Officer"),
        });
        toast({
          title: `DTC Loan Officer disabled for ${user.name}`,
          status: "warning",
          position: "top",
          isClosable: true,
        });
      }
    } catch (err) {
      toast({ title: "Error: " + err, status: "error", position: "top", isClosable: true });
    }
  };

  /**
   * External Demo (partner test drives of Origin). Enabling it also grants
   * CRM access in the same write, so one toggle fully provisions a demo
   * account. Disabling only clears the demo flag — deliberately: the admin
   * decides whether to also revoke CRM access or deactivate the account
   * (see docs/origin-demo-access-runbook.md).
   */
  const handleExternalDemoChange = async (checked) => {
    try {
      const userDoc = doc(db, "users", user.uid);
      if (checked) {
        const updates = { externalDemo: true, CRMUser: true };
        if (!user.CRMRole) {
          updates.CRMRole = "Loan Officer";
        }
        await updateDoc(userDoc, updates);
        toast({
          title: `External Demo enabled for ${user.name}`,
          description:
            "Account is now confined to Origin with outbound email/SMS and campaign enrollment blocked. Seed demo data next (see the demo runbook).",
          status: "success",
          position: "top",
          isClosable: true,
        });
      } else {
        await updateDoc(userDoc, { externalDemo: false });
        toast({
          title: `External Demo disabled for ${user.name}`,
          description:
            "The account still has CRM access — revoke CRM User or deactivate the account if this was an external tester.",
          status: "warning",
          position: "top",
          isClosable: true,
        });
      }
    } catch (err) {
      toast({ title: "Error: " + err, status: "error", position: "top", isClosable: true });
    }
  };

  const handleLOLevelChange = async (checked, fieldDescr, field) => {
    try {
      const status = checked ? "success" : "warning";
      const title = checked
        ? `${fieldDescr} enabled for ${user.name} `
        : `${fieldDescr} disabled for ${user.name} `;
      const description = checked
        ? `Gotchas, Loan Narrative & Assets and Income will be required for new loan submissions for this user`
        : `Gotchas, Loan Narrative & Assets and Income will be NOT required for new loan submissions for this user`;
      await updateDoc(doc(db, "users", user.uid), {
        [field]: checked,
      });
      toast({ title, status, description, position: "top", isClosable: true });
    } catch (err) {
      toast({ title: "Error: " + err, status: "error", position: "top", isClosable: true });
    }
  };

  const handleUpdateSwagBudget = async () => {
    try {
      await updateDoc(doc(db, "users", user.uid), {
        "Swag Budget": swagBudget,
      });
      toast({
        title: `Swag budget updated for ${user.name} `,
        status: "success",
        position: "top",
        isClosable: true,
      });
    } catch (err) {
      toast({ title: "Error: " + err, status: "error", position: "top", isClosable: true });
    }
  };

  const handleSetBranch = async (e) => {
    if (e) {
      await updateDoc(doc(db, "users", user.uid), {
        Branch: e.value,
      });
      toast({
        title: user.name + " assigned to " + e.value + " branch",
        status: "success",
        position: "top",
        isClosable: true,
      });
    } else {
      await updateDoc(doc(db, "users", user.uid), {
        Branch: null,
      });
      toast({
        title: user.name + "'s branch removed",
        status: "warning",
        position: "top",
        isClosable: true,
      });
    }
  };

  const handleEOSPermissionChange = async (permission, status) => {
    setLoadingPermissionId(`${user.uid}-${permission}`);
    try {
      await updateUserPermission(user.uid, permission, status);
      toast({
        title: `${
          permission === "isEOSUser" ? "EOS Access" : "Can Present Meetings"
        } ${status ? "granted" : "revoked"} for ${user.name}`,
        status: status ? "success" : "warning",
        position: "top",
        isClosable: true,
      });
    } catch (err) {
      toast({ title: "Error: " + err.message, status: "error", position: "top", isClosable: true });
    } finally {
      setLoadingPermissionId(null);
    }
  };

  const roles = Array.isArray(user.Roles) ? user.Roles.filter(Boolean) : [];
  const isDTC =
    user.isDTCLoanOfficer ||
    (Array.isArray(user.Roles) && user.Roles.includes("DTC Loan Officer")) ||
    false;

  return (
    <Box bg={cardBg} borderWidth="1px" borderColor={borderColor} borderRadius="lg" p={5} shadow="sm">
      {/* Header: who am I editing */}
      <Flex justify="space-between" align="flex-start" mb={5} gap={3} flexWrap="wrap">
        <HStack spacing={3} align="center">
          <Avatar size="md" name={user.name} src={user.photo} />
          <VStack align="flex-start" spacing={0}>
            <Heading size="sm" m={0}>
              {user.name || "No Name"}
            </Heading>
            {user.email && (
              <Text fontSize="xs" color={subtleColor}>
                {user.email}
              </Text>
            )}
            {roles.length > 0 && (
              <Wrap spacing={1} mt={1}>
                {roles.map((role, j) => (
                  <WrapItem key={`role-${j}`}>
                    <Tag size="sm" variant="subtle" colorScheme="gray">
                      {role}
                    </Tag>
                  </WrapItem>
                ))}
              </Wrap>
            )}
          </VStack>
        </HStack>
        <HStack spacing={2}>
          {onOpenProfile && (
            <Button
              size="xs"
              variant="outline"
              leftIcon={<ExternalLinkIcon />}
              onClick={() => onOpenProfile(user.uid)}
            >
              Open full profile
            </Button>
          )}
          {allowDeactivate && onRequestDeactivate && (
            <Tooltip label="Deactivate user" placement="top">
              <IconButton
                aria-label="Deactivate user"
                colorScheme="red"
                size="sm"
                variant="ghost"
                icon={<BsFillTrashFill />}
                onClick={() => onRequestDeactivate(user.uid, user.name)}
              />
            </Tooltip>
          )}
        </HStack>
      </Flex>

      <VStack align="stretch" spacing={4}>
        <SimpleGrid columns={{ base: 1, lg: 3 }} spacing={4}>
          {/* Hub Roles */}
          <SettingSection title="Hub Roles" bg={sectionBg} borderColor={borderColor}>
            <SettingRow label="Super Admin" tooltip="Super Admins can manage users">
              <Switch
                isChecked={!!user.SuperAdmin}
                onChange={(e) => handleAdminChange(e.target.checked, "Super Admin", "SuperAdmin")}
              />
            </SettingRow>
            <SettingRow label="Submissions Admin">
              <Switch
                isChecked={!!user.isAdmin}
                onChange={(e) =>
                  handleAdminChange(e.target.checked, "Submissions Admin", "isAdmin")
                }
              />
            </SettingRow>
            <SettingRow label="Marketing Admin">
              <Switch
                isChecked={!!user.MarketingAdmin}
                onChange={(e) =>
                  handleAdminChange(e.target.checked, "Marketing Admin", "MarketingAdmin")
                }
              />
            </SettingRow>
          </SettingSection>

          {/* CRM */}
          <SettingSection title="CRM" bg={sectionBg} borderColor={borderColor}>
            <SettingRow
              label="CRM User"
              tooltip="CRM Users can access the Origin CRM / My Deals page"
            >
              <Switch
                colorScheme="purple"
                isChecked={user.CRMUser || false}
                onChange={(e) => handleCRMUserChange(e.target.checked)}
              />
            </SettingRow>
            <SettingRow
              label="CRM Role"
              tooltip="Loan Officer or CRM Admin - determines CRM access level"
            >
              <FormControl w="auto">
                <Select
                  size="sm"
                  minW="150px"
                  value={user.CRMRole || ""}
                  onChange={(e) => handleCRMRoleChange(e.target.value)}
                  isDisabled={!user.CRMUser}
                  placeholder="Select role..."
                >
                  <option value="Loan Officer">Loan Officer</option>
                  <option value="CRM Admin">CRM Admin</option>
                </Select>
              </FormControl>
            </SettingRow>
            <SettingRow
              label="DTC LO"
              tooltip="DTC Loan Officers must provide a reason when marking deals as Lost. Email notification sent to LostDeal@future.loans"
            >
              <Switch
                colorScheme="orange"
                isChecked={isDTC}
                onChange={(e) => handleDTCLoanOfficerChange(e.target.checked)}
                isDisabled={!user.CRMUser}
              />
            </SettingRow>
            <SettingRow
              label="External Demo"
              tooltip="For non-employee test drives of Origin. Confines the account to the CRM only (all other Hub pages hidden and blocked), disables outbound email/SMS and campaign enrollment, and hides company-wide surfaces like the Leaderboard. Enabling also grants CRM User access."
            >
              <Switch
                colorScheme="cyan"
                isChecked={user.externalDemo === true}
                onChange={(e) => handleExternalDemoChange(e.target.checked)}
              />
            </SettingRow>
          </SettingSection>

          {/* EOS */}
          <SettingSection title="EOS" bg={sectionBg} borderColor={borderColor}>
            <SettingRow label="EOS Access" tooltip="Grants access to the EOS module">
              <Switch
                colorScheme="green"
                isChecked={user.isEOSUser || false}
                onChange={(e) =>
                  handleEOSPermissionChange("isEOSUser", e.target.checked)
                }
                isDisabled={loadingPermissionId === `${user.uid}-isEOSUser`}
              />
            </SettingRow>
            <SettingRow
              label="Can Present Meetings"
              tooltip="Allows user to present L10 meetings"
            >
              <Switch
                colorScheme="blue"
                isChecked={user.canPresentMeetings || false}
                onChange={(e) =>
                  handleEOSPermissionChange("canPresentMeetings", e.target.checked)
                }
                isDisabled={loadingPermissionId === `${user.uid}-canPresentMeetings`}
              />
            </SettingRow>
          </SettingSection>
        </SimpleGrid>

        {/* Less used — de-emphasized (moved out of the primary controls) */}
        <SettingSection title="Less used" bg={sectionBg} borderColor={borderColor}>
          <SimpleGrid columns={{ base: 1, md: 3 }} spacing={4} alignItems="start">
            <SettingRow
              label="Level 1"
              tooltip="Level 1 users will be required to fill out Gotchas, Loan Narrative & Assets and Income when submitting new loans"
            >
              <Switch
                isChecked={user["Level1"] || false}
                onChange={(e) => handleLOLevelChange(e.target.checked, "Level 1", "Level1")}
              />
            </SettingRow>

            <SettingRow label="Swag Budget">
              <HStack spacing={1}>
                <Input
                  width="90px"
                  size="sm"
                  type="number"
                  value={swagBudget}
                  onChange={(e) => setSwagBudget(e.target.value)}
                />
                <IconButton
                  aria-label="Save swag budget"
                  icon={<FiCheck />}
                  size="sm"
                  variant="ghost"
                  color="green"
                  onClick={handleUpdateSwagBudget}
                  isDisabled={String(swagBudget) === String(user["Swag Budget"] ?? "")}
                />
              </HStack>
            </SettingRow>

            <Box>
              <Text fontSize="sm" fontWeight="medium" mb={1}>
                Branch
              </Text>
              <FormControl>
                <CreatableSelect
                  value={user.Branch ? { label: user.Branch, value: user.Branch } : null}
                  isClearable
                  options={branchOptions}
                  onChange={(e) => handleSetBranch(e)}
                  placeholder="Select or create a branch..."
                  size="sm"
                />
              </FormControl>
            </Box>
          </SimpleGrid>
        </SettingSection>
      </VStack>
    </Box>
  );
}
